import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CheckCircle, Cancel, Warning, ErrorOutline } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { greens, reds, oranges, greys } from '@/styles/colors';
import { differenceInCalendarDays, parseISO } from 'date-fns';

const EXPIRY_WARNING_DAYS = 30;

export const evaluateDocStatus = (doc) => {
    if (!doc) return { state: 'missing', daysToExpiry: null };
    if (!doc.expiration_date) return { state: 'valid', daysToExpiry: null };

    const expiresAt = typeof doc.expiration_date === 'string' ? parseISO(doc.expiration_date) : doc.expiration_date;
    const days = differenceInCalendarDays(expiresAt, new Date());

    if (days < 0) return { state: 'expired', daysToExpiry: days };
    if (days <= EXPIRY_WARNING_DAYS) return { state: 'expiring', daysToExpiry: days };
    return { state: 'valid', daysToExpiry: days };
};

const computeIconConfig = (status, doc, label) => {
    switch (status.state) {
        case 'valid':
            return {
                Icon: CheckCircle,
                color: greens.primary,
                statusTip: doc?.expiration_date
                    ? `${label} valid (expires in ${status.daysToExpiry}d)`
                    : `${label} on file`,
                actionVerb: 'replace',
            };
        case 'expiring':
            return {
                Icon: Warning,
                color: oranges.primary,
                statusTip: `${label} expires in ${status.daysToExpiry}d`,
                actionVerb: 'replace',
            };
        case 'expired':
            return {
                Icon: ErrorOutline,
                color: reds.primary,
                statusTip: `${label} expired ${Math.abs(status.daysToExpiry)}d ago`,
                actionVerb: 'replace',
            };
        case 'missing':
        default:
            return {
                Icon: Cancel,
                color: reds.primary,
                statusTip: `${label} missing`,
                actionVerb: 'upload',
            };
    }
};

const DocStatusIcon = ({ doc, label, fontSize = 'small', onClick }) => {
    const status = useMemo(() => evaluateDocStatus(doc), [doc]);
    const { Icon, color, statusTip, actionVerb } = useMemo(
        () => computeIconConfig(status, doc, label),
        [status, doc, label]
    );
    const tip = onClick ? `${statusTip} — click to ${actionVerb}` : statusTip;

    return (
        <Tooltip title={tip} placement="top">
            <Icon
                fontSize={fontSize}
                onClick={onClick}
                css={css`
                    color: ${color};
                    cursor: ${onClick ? 'pointer' : 'default'};
                `}
            />
        </Tooltip>
    );
};

// --- Hover-affordance wrapper: keeps the bare icon but adds a clear "click target" surface ---

const HoverSurface = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    border: 1px solid transparent;
    background: transparent;
    color: ${greys[2]};
    cursor: pointer;
    font: inherit;
    transition: background-color 120ms ease, border-color 120ms ease;

    & .doc-action-hint {
        opacity: 0;
        transition: opacity 120ms ease;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &:hover {
        background: ${greys[5]};
        border-color: ${greys[4]};
    }

    &:hover .doc-action-hint {
        opacity: 1;
    }

    &:focus-visible {
        outline: 2px solid ${(p) => p.$accent};
        outline-offset: 1px;
    }
`;

export const DocStatusHoverButton = ({ doc, label, onClick }) => {
    const status = useMemo(() => evaluateDocStatus(doc), [doc]);
    const { color, statusTip, actionVerb } = useMemo(() => computeIconConfig(status, doc, label), [status, doc, label]);
    const tip = `${statusTip} — click to ${actionVerb} ${label}`;
    const actionLabel = actionVerb === 'upload' ? 'Upload' : 'Replace';

    return (
        <Tooltip title={tip} placement="top">
            <HoverSurface
                type="button"
                onClick={onClick}
                $accent={color}
                css={css`
                    color: ${color};
                `}
            >
                <DocStatusIcon doc={doc} label={label} />
                <span className="doc-action-hint">{actionLabel}</span>
            </HoverSurface>
        </Tooltip>
    );
};
